import { FC, useMemo } from 'react';
import styled from 'styled-components/native';

import IconNewNavRight from '../../../assets/svg/IconNewNavRight';
import IconPackage from '../../../assets/svg/IconPackage';
import IconWarning from '../../../assets/svg/IconWarning';
import { coral, peacock, warrior } from '../../../constants/color';
import NoScaleText from '../../../elements/NoScaleText';
import Touchable from '../../../elements/Touchable/Touchable';
import { useTranslation } from '../../../i18n';
import { DeliveryMode } from '../../../types/Common';
import { getIsTW } from '../../../utils/countryInfo';
import { useData } from './hooks';

interface Props {
  isLoading: boolean;
  setIsModalOpen: (v: boolean) => void;
}

const SelectSection: FC<Props> = ({ setIsModalOpen }) => {
  const { t } = useTranslation();
  const { selectedMethod, methods } = useData();

  const deliveryModeText = useMemo(() => {
    switch (selectedMethod) {
      case DeliveryMode.HOME_DELIVERY:
        return getIsTW() ? t('home-delivery') : t('home-delivery-for-gc');
      case DeliveryMode.CONVENIENCE_STORE_PICKUP:
        return t('convenience-store-pickup');
      default:
        return;
    }
  }, [selectedMethod, t]);

  const renderShippingSection = () => {
    return selectedMethod ? (
      methods.find(
        method =>
          method.id === selectedMethod && method.cvs_delivery_errors?.length > 0
      ) ? (
        <>
          <IconWarning size={20} fillColor={coral} />
          <S.Warning>{t('reselect-delivery-mode-alert')}</S.Warning>
          <IconNewNavRight size={16} fillColor={warrior} />
        </>
      ) : (
        <>
          <S.Description>{`${deliveryModeText}`}</S.Description>
          <IconNewNavRight size={16} fillColor={warrior} />
        </>
      )
    ) : (
      <>
        <S.Description>{t('select-shipping-method')}</S.Description>
        <IconNewNavRight size={16} fillColor={warrior} />
      </>
    );
  };

  return (
    <>
      <S.SectionWrapper
        onPress={() => setIsModalOpen(true)}
        testID="CartShippingSectionBtn"
      >
        <S.Left>
          <IconPackage size={24} fillColor={peacock} />
          <S.Title>{t('deliveryMethods.title')}</S.Title>
        </S.Left>
        <S.Right>{renderShippingSection()}</S.Right>
      </S.SectionWrapper>
    </>
  );
};

const S = {
  SectionWrapper: styled(Touchable)`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 15px;
  `,
  Title: styled(NoScaleText)`
    font-size: 15px;
    margin-left: 10px;
    color: ${({ theme }) => theme.palette.warrior};
  `,
  Description: styled(NoScaleText)`
    font-size: 15px;
    margin-right: 10px;
    color: ${({ theme }) => theme.palette.neutralDark};
    border: 2px solid transparent;
  `,
  Warning: styled(NoScaleText)`
    font-size: 15px;
    margin-left: 5px;
    margin-right: 10px;
    color: ${({ theme }) => theme.palette.coral};
    border: 1px solid transparent;
  `,
  Left: styled.View`
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  `,
  Right: styled.View`
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  `,
  DiscountLabelBorder: styled.View`
    align-self: center;
    flex-direction: row;
    border-radius: 23px;
    border-color: ${coral};
    border-width: 1px;
    height: 24px;
    align-items: center;
    justify-content: center;
    padding-left: 10px;
    padding-right: 10px;
    margin-right: 15px;
  `,
  DiscountLabelText: styled(NoScaleText)`
    font-size: 12px;
    color: ${coral};
  `,
  ShippingFeeHint: styled(NoScaleText)`
    border: ${coral} 1px solid;
    border-radius: 12px;
    color: ${coral};
    font-size: 12px;
    line-height: 20px;
    margin-right: 5;
    padding: 0 10px;
  `,
};

export default SelectSection;
